import {
  CallInRequest,
  colors,
  getSignedOnByPracticeDate,
  InsuranceCoverageRequest,
  InsuranceProvider,
  Log,
  LogEntry,
  PopulatedAssessment,
  PopulatedUser,
  WithId,
} from '@hb/shared'

const getRequestLogItem = (request: InsuranceCoverageRequest): LogEntry => ({
  text: 'Insurance Coverage Request',
  updatedBy: request.requestedBy,
  updatedByGroup: 'admin',
  updatedOn: request.requestedOn,
  color: colors.indigo.hex,
  isInjected: true,
})

const getCallInRequestsLogItem = (request: CallInRequest): LogEntry => ({
  text: 'Call-In Request',
  updatedBy: request.by,
  updatedOn: request.on,
  createdBy: request.by,
  createdOn: request.on,
  updatedByGroup: 'admin',
  color: colors.indigo.hex,
  isInjected: true,
})

export const injectPatientLogItems = (
  log: Log | null | undefined,
  user: PopulatedUser | null,
  insurers: WithId<InsuranceProvider>[],
) => {
  const now = Date.now()
  const { insurancePlans: plans, joinedOn } = user ?? {}
  const {
    additional: additionalPlans,
    requests: coverageRequests,
    primary,
    secondary,
  } = plans ?? {}
  const injected = { ...log }
  const patientId = user?.id
  // const primaryTerminationDate = primaryCoverage?.terminationDate
  // const medicaidTerminationDate = medicaidCoverage?.terminationDate
  const primaryTerminationDate = primary?.terminationDate
  if (primaryTerminationDate && primaryTerminationDate < now) {
    injected[primaryTerminationDate] = {
      text: 'Primary insurance coverage terminated',
      updatedBy: patientId,
      updatedByGroup: 'patient',
      updatedOn: primaryTerminationDate,
      color: colors.red.hex,
      isInjected: true,
    }
  }
  const setAsPrimaryHistory = primary?.history?.filter(h => h.type === 'setAsPrimary') ?? []
  const lastSetAsPrimary = setAsPrimaryHistory[setAsPrimaryHistory.length - 1]
  if (lastSetAsPrimary) {
    injected[lastSetAsPrimary.on] = {
      text: 'Primary insurance set',
      updatedBy: lastSetAsPrimary.by,
      updatedByGroup: 'admin',
      updatedOn: lastSetAsPrimary.on,
      color: colors.green.hex,
      isInjected: true,
    }
  }

  const secondaryTerminationDate = secondary?.terminationDate
  if (secondaryTerminationDate && secondaryTerminationDate < now) {
    injected[secondaryTerminationDate] = {
      text: 'Medicaid coverage terminated',
      updatedBy: patientId,
      updatedByGroup: 'patient',
      updatedOn: secondaryTerminationDate,
      color: colors.red.hex,
      isInjected: true,
    }
  }

  Object.values(additionalPlans ?? {}).forEach(plan => {
    Object.values(plan.callInRequests ?? {}).forEach(req => {
      injected[req.on] = getCallInRequestsLogItem(req)
    })
    if (plan.fromRequest) {
      injected[plan.fromRequest.requestedOn] = getRequestLogItem(plan.fromRequest)
    }
    if (plan.createdOn) {
      const insurer = insurers.find(i => i.id === plan.insuranceProviderId)
      injected[plan.createdOn] = {
        text: `Additional Plan Created${insurer ? `\n${insurer.name} | ${plan.memberId || 'No member ID'}` : ''}`,
        createdBy: plan.createdBy,
        updatedBy: plan.updatedBy,
        createdByGroup: plan.updatedByGroup || 'patient',
        updatedByGroup: plan.updatedByGroup || 'patient',
        updatedOn: plan.createdOn,
        createdOn: plan.createdOn,
        color: colors.green.hex,
        isInjected: true,
      }
    }
    if (plan.terminationDate && plan.terminationDate < now) {
      injected[plan.terminationDate] = {
        text: 'Additional plan terminated',
        updatedBy: patientId,
        updatedByGroup: 'patient',
        updatedOn: plan.terminationDate,
        color: colors.red.hex,
        isInjected: true,
      }
    }
  })

  Object.values(coverageRequests ?? {}).forEach(req => {
    injected[req.requestedOn] = getRequestLogItem(req)
  })

  if (joinedOn) {
    injected[joinedOn] = {
      text: 'User joined (first sign in)',
      updatedBy: patientId,
      updatedByGroup: 'patient',
      updatedOn: joinedOn,
      color: colors.green.hex,
      isInjected: true,
    }
  }
  return injected
}

export const injectAssessmentLogItems = (
  log: Log | null | undefined,
  user: PopulatedUser | null,
  assessment: PopulatedAssessment | null,
  insurers: WithId<InsuranceProvider>[],
) => {
  const {
    signedOnDate,
    patientId,
    createdByGroup,
    createdOn,
    createdBy,
    practiceStageHistory,
    practiceStages,
  } = assessment ?? {}
  const injected = { ...log }
  if (signedOnDate) {
    injected[signedOnDate] = {
      text: 'Sign-on form completed',
      createdBy: patientId,
      color: colors.green.hex,
      updatedBy: patientId,
      updatedOn: signedOnDate,
      createdOn: signedOnDate,
      createdByGroup: 'patient',
      isInjected: true,
    }
  }

  const signedOnByPracticeDate = getSignedOnByPracticeDate(
    practiceStages ?? [],
    practiceStageHistory,
  )
  if (signedOnByPracticeDate) {
    const fallbackDate = signedOnDate ? signedOnDate + 1 : 0
    const usedDate = signedOnByPracticeDate === 'unknown' ? fallbackDate : signedOnByPracticeDate.on
    const usedBy = signedOnByPracticeDate === 'unknown' ? patientId : signedOnByPracticeDate.by
    const usedByGroup =
      signedOnByPracticeDate === 'unknown' ? 'practice' : signedOnByPracticeDate.group
    injected[usedDate] = {
      text: `Patient signed on by practice${signedOnByPracticeDate === 'unknown' ? ' (unknown date)' : ''}`,
      createdBy: usedBy,
      color: colors.green.hex,
      updatedBy: usedBy,
      updatedOn: usedDate,
      createdOn: usedDate,
      createdByGroup: usedByGroup,
      notes:
        typeof signedOnByPracticeDate === 'object'
          ? (signedOnByPracticeDate.notes ?? undefined)
          : undefined,
      isInjected: true,
    }
  }

  if (createdOn && createdOn !== signedOnDate) {
    injected[createdOn] = {
      text: 'Pregnancy created',
      updatedBy: createdBy,
      createdBy,
      updatedByGroup: createdByGroup || 'patient',
      updatedOn: createdOn,
      color: colors.green.hex,
      isInjected: true,
    }
  }
  if (assessment?.submittedOn) {
    injected[assessment.submittedOn] = {
      text: 'Questionnaire submitted',
      updatedByGroup: assessment.submittedByGroup || 'patient',
      updatedBy: assessment.submittedBy || 'none',
      updatedOn: assessment.submittedOn,
      color: colors.indigo.hex,
      isInjected: true,
    }
  }
  if (assessment?.correctionsUpdatedBy && assessment.correctionsUpdatedOn) {
    injected[assessment.correctionsUpdatedOn] = {
      text: 'Answers corrected',
      updatedBy: assessment.correctionsUpdatedBy,
      updatedByGroup: 'admin',
      updatedOn: assessment.correctionsUpdatedOn,
      color: colors.indigo.hex,
      isInjected: true,
    }
  }
  if (assessment?.draftSavedOn && assessment.draftSavedBy) {
    injected[assessment.draftSavedOn] = {
      text: 'Assessment draft saved',
      updatedBy: assessment.draftSavedBy,
      updatedByGroup: 'admin',
      updatedOn: assessment.draftSavedOn,
      color: colors.indigo.hex,
      isInjected: true,
    }
  }

  if (assessment?.previousResults && assessment.previousResults.length > 0) {
    assessment.previousResults.forEach(res => {
      const { insuranceProviderId } = res
      const insurer = insurers.find(i => i.id === insuranceProviderId)
      if (res.sentOn && res.sentBy) {
        injected[res.sentOn] = {
          text: res.insuranceProviderId
            ? `Previous${insurer ? ` ${insurer.name}` : ''} assessment sent`
            : 'Previous assessment sent',
          updatedBy: res.sentBy,
          updatedByGroup: 'admin',
          updatedOn: res.sentOn,
          color: colors.indigo.hex,
          isInjected: true,
        }
      }
    })
  }

  if (assessment?.sentBy && assessment.sentOn) {
    const insurer = insurers.find(i => i.id === assessment.resultsInsurerId)
    injected[assessment.sentOn] = {
      text: insurer ? `${insurer.name} Assessment Sent` : 'Assessment sent',
      updatedBy: assessment.sentBy,
      updatedByGroup: 'admin',
      updatedOn: assessment.sentOn,
      color: colors.indigo.hex,
      isInjected: true,
    }
  }
  if (assessment?.resultsViewedOn) {
    const insurer = insurers.find(i => i.id === assessment.resultsInsurerId)
    injected[assessment.resultsViewedOn] = {
      text: insurer ? `${insurer.name} Assessment Viewed` : 'Assessment viewed',
      updatedBy: assessment.patientId ?? '',
      updatedByGroup: 'patient',
      updatedOn: assessment.resultsViewedOn,
      color: colors.green.hex,
      isInjected: true,
    }
  }
  return injectPatientLogItems(injected, user, insurers)
}
